import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, ConfirmModal, Textarea } from "../../ui";
import { errorMessageOf, scholarshipsApi } from "../../../api/scholarships";
import { EVALUATOR_KEY } from "./EvaluatorPages";

// The committee's name, read into the rules; loan calls pass their own
const rulesOf = (committee) => [
  `I will follow the rules and procedures that govern the ${committee}.`,
  "I will keep every application, document and mark confidential and use them only for this review.",
  "I will review each applicant fairly, on the published criteria alone.",
  "I will remove from my list any applicant I have a personal, family or professional relationship with, or any other conflict of interest.",
];

// The scholarships' committee calls; the abroad loan pages pass abroadEvaluatorApi, which has the same shape
const SCHOLARSHIP_COMMITTEE = {
  consent: (id) => scholarshipsApi.post(`evaluator/scholarships/${id}/consent`),
  removeApplicant: (applicationId, body) => scholarshipsApi.put(`evaluator/applications/${applicationId}/conflict`, body),
  restoreApplicant: (applicationId) => scholarshipsApi.delete(`evaluator/applications/${applicationId}/conflict`),
};

// Given once per call before its applicants are shown; it covers every applicant in that call
export function ConsentCard({ scholarshipId, onConsented, api = SCHOLARSHIP_COMMITTEE, committee = "Scholarship Review Committee" }) {
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const consent = async () => {
    setBusy(true);
    try {
      await api.consent(scholarshipId);
      await queryClient.invalidateQueries({ queryKey: EVALUATOR_KEY });
      onConsented?.();
    } catch (error) {
      console.error(`Failed to record consent for call ${scholarshipId}:`, error);
      toast.error(errorMessageOf(error, "Failed to record your consent. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Consent">
      <p className="text-sm text-slate-700">As a member of the {committee} for this call:</p>
      <ul className="mt-3 flex list-disc flex-col gap-1.5 pl-5 text-sm text-slate-700">
        {rulesOf(committee).map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-slate-500">You give this once for all applicants in this call. If you have a conflict with an applicant, remove them from your list; you can put them back later.</p>
      <label className="mt-4 flex items-start gap-2 text-sm text-slate-800">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />I have read these rules and agree to follow them.
      </label>
      <div className="mt-4 flex justify-end">
        <Button icon={<ShieldCheckIcon className="h-4 w-4" />} onClick={consent} loading={busy} disabled={!agreed}>
          Give consent
        </Button>
      </div>
    </Card>
  );
}

// Takes an applicant the evaluator has a conflict with off their list; the other members review them
export function RemoveApplicantModal({ applicant, applicationId, onClose, onRemoved, api = SCHOLARSHIP_COMMITTEE }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.removeApplicant(applicationId, { ...(note.trim() && { note: note.trim() }) });
      await queryClient.invalidateQueries({ queryKey: EVALUATOR_KEY });
      toast.success("Applicant removed from your list. HEC can see the conflict.");
      setNote("");
      onRemoved?.();
    } catch (error) {
      console.error(`Failed to remove application ${applicationId} for a conflict:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the applicant. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmModal
      open={Boolean(applicationId)}
      onClose={onClose}
      onConfirm={remove}
      loading={busy}
      destructive
      title={`Remove ${applicant || "this applicant"}?`}
      description="You have a conflict of interest with this applicant. The other committee members will review them instead. You can put them back later."
      confirmLabel="Remove"
    >
      <Textarea label="What is the conflict? (optional)" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
    </ConfirmModal>
  );
}

export async function restoreApplicant(queryClient, applicationId, api = SCHOLARSHIP_COMMITTEE) {
  try {
    await api.restoreApplicant(applicationId);
    await queryClient.invalidateQueries({ queryKey: EVALUATOR_KEY });
    toast.success("Applicant put back on your list.");
  } catch (error) {
    console.error(`Failed to put application ${applicationId} back:`, error);
    toast.error(errorMessageOf(error, "Failed to put the applicant back. Please try again."));
  }
}
